import type { CssVariablesMap } from '@shared/app-config/types'
import { uuid } from '@shared/util/uuid'

/**
 * Adopted stylesheets registry — one sheet per data-role.
 * Sheets in adoptedStyleSheets always cascade AFTER all <style>/<link> elements,
 * so custom CSS/variables always win regardless of when child apps inject styles.
 */
const adoptedSheets = new Map<string, CSSStyleSheet>()

/**
 * Adopted role ordering — later entries cascade with higher priority.
 * Custom CSS must be last so it can override variables.
 */
const ADOPTED_ORDER = ['wippy-host-css-variables', 'wippy-host-styles-custom-css'] as const
const ADOPTED_ROLES = new Set<string>(ADOPTED_ORDER)

/**
 * Extract @import rules from CSS.
 * Handles single-line CSS where @import and declarations are on the same line.
 * Walks character-by-character to correctly skip quoted strings and url() contents.
 */
function extractImports(css: string): { imports: string, declarations: string } {
  const imports: string[] = []
  const rest: string[] = []

  for (const line of css.split('\n')) {
    let remaining = line
    // Pull @import statements from the front of each line
    while (remaining.trimStart().startsWith('@import ') || remaining.trimStart().startsWith('@import\t')) {
      const trimmed = remaining.trimStart()
      // Walk to find the terminating semicolon, skipping quoted/parenthesised content
      let i = 7 // past "@import"
      while (i < trimmed.length) {
        const ch = trimmed[i]
        if (ch === '\'' || ch === '"') {
          // skip quoted string
          const q = ch
          i++
          while (i < trimmed.length && trimmed[i] !== q) i++
          i++ // closing quote
        }
        else if (ch === '(') {
          // skip parenthesised content (e.g. url(...))
          let depth = 1
          i++
          while (i < trimmed.length && depth > 0) {
            if (trimmed[i] === '(') {
              depth++
            }
            else if (trimmed[i] === ')') {
              depth--
            }
            else if (trimmed[i] === '\'' || trimmed[i] === '"') {
              const q2 = trimmed[i]
              i++
              while (i < trimmed.length && trimmed[i] !== q2) i++
            }
            i++
          }
        }
        else if (ch === ';') {
          i++ // include the semicolon
          break
        }
        else {
          i++
        }
      }
      imports.push(trimmed.substring(0, i).trim())
      remaining = trimmed.substring(i)
    }
    if (remaining.trim())
      rest.push(remaining)
  }

  return {
    imports: imports.join('\n'),
    declarations: rest.join('\n').trim(),
  }
}

/** @import rules don't work in constructable stylesheets — split them out. */
const splitImports = extractImports

/** Add or update an adopted stylesheet (always cascades after <style>/<link>). */
function setAdoptedSheet(role: string, css: string) {
  let sheet = adoptedSheets.get(role)
  if (!sheet) {
    sheet = new CSSStyleSheet()
    adoptedSheets.set(role, sheet)
    // Rebuild in defined order so custom CSS always has highest priority
    const others = document.adoptedStyleSheets.filter(s =>
      ![...adoptedSheets.values()].includes(s),
    )
    const ordered = ADOPTED_ORDER
      .map(r => adoptedSheets.get(r))
      .filter((s): s is CSSStyleSheet => !!s)
    document.adoptedStyleSheets = [...others, ...ordered]
  }
  sheet.replaceSync(css)
}

/** Replace an existing element with the same data-role, or append if none exists. */
function replaceOrAppend(parent: ParentNode, el: Element, dataRole: string) {
  const existing = parent.querySelector(`[data-role="${dataRole}"]`)
  if (existing)
    existing.replaceWith(el)
  else
    parent.appendChild(el)
}

export function createStyle(style: string, id: string = uuid(), parent: ParentNode = document.head) {
  const dataRole = `wippy-host-styles-${id}`

  if (ADOPTED_ROLES.has(dataRole)) {
    const { imports, declarations } = splitImports(style)

    // @import rules stay in <head> (not supported in constructable stylesheets)
    const importRole = `${dataRole}-imports`
    if (imports) {
      const importEl = document.createElement('style')
      importEl.setAttribute('data-role', importRole)
      importEl.appendChild(document.createTextNode(imports))
      replaceOrAppend(parent, importEl, importRole)
    }
    else {
      // Remove stale import element if custom CSS no longer has @imports
      parent.querySelector(`[data-role="${importRole}"]`)?.remove()
    }

    // Declarations go to adoptedStyleSheets — always wins over <style>/<link>
    // Always set (even if empty) to clear stale content from a previous call
    setAdoptedSheet(dataRole, declarations)

    return
  }

  const styleElement = document.createElement('style')
  styleElement.setAttribute('data-role', dataRole)
  styleElement.appendChild(document.createTextNode(style))
  replaceOrAppend(parent, styleElement, dataRole)
}

export function createStyleLink(styleUrl: string, id: string = uuid(), parent: ParentNode = document.head) {
  const dataRole = `wippy-host-styles-${id}`
  const styleElement = document.createElement('link')
  styleElement.setAttribute('data-role', dataRole)
  styleElement.setAttribute('rel', 'stylesheet')
  styleElement.setAttribute('href', styleUrl)
  replaceOrAppend(parent, styleElement, dataRole)
}

/**
 * Accepts a flat or structured CSS variables object.
 *
 * Keys without `--` prefix get it added automatically.
 * Special keys `@dark` and `@light` map to `prefers-color-scheme` media queries.
 *
 * Example input:
 * ```json
 * {
 *   "p-primary": "blue",
 *   "--p-secondary": "green",
 *   "@dark":  { "p-primary": "lightblue", "p-surface-0": "#111" },
 *   "@light": { "p-surface-0": "white" }
 * }
 * ```
 */
export function createCssVariables(
  cssVariables: CssVariablesMap,
  _parent: ParentNode = document.head,
) {
  const toVar = (key: string, value: string) =>
    `${key.startsWith('--') ? key : `--${key}`}: ${value};`

  const toBlock = (vars: Record<string, string>) =>
    Object.entries(vars).map(([k, v]) => toVar(k, v)).join('\n')

  const rootVars: Record<string, string> = {}
  const mediaBlocks: string[] = []

  for (const [key, value] of Object.entries(cssVariables)) {
    if ((key === '@dark' || key === '@light') && typeof value === 'object' && value) {
      const block = toBlock(value as Record<string, string>)
      if (block) {
        const scheme = key === '@dark' ? 'dark' : 'light'
        mediaBlocks.push(`@media (prefers-color-scheme: ${scheme}) { :root { ${block} } }`)
      }
    }
    else if (typeof value === 'string') {
      rootVars[key] = value
    }
  }

  const parts: string[] = []
  if (Object.keys(rootVars).length > 0)
    parts.push(`:root { ${toBlock(rootVars)} }`)
  parts.push(...mediaBlocks)
  if (parts.length === 0)
    return

  // CSS variables are pure declarations — go straight to adoptedStyleSheets
  setAdoptedSheet('wippy-host-css-variables', parts.join('\n'))
}
